/*
 * Automation — Tester-only console that 1-clicks through the long
 * completion flows so QA doesn't spend an hour clicking through
 * modules → exam → blueprint → worksheets on every test account.
 *
 * Every action calls the tester_automation router which internally
 * hits the same public endpoints a real founder does (via httpx +
 * ASGITransport). That means:
 *   • auth, tier gating, rate limits, and validation rules are ALL
 *     re-used verbatim
 *   • pre-existing dependencies are respected (e.g. Final Exam is
 *     blocked with "Please complete the Core Modules first." when
 *     any core module is still incomplete)
 *   • no completion logic is duplicated on the backend
 *
 * Members never see the sidebar entry (injected in AppShell.jsx only
 * for role='tester' / super_admin) and the API layer 403s if they
 * hit it directly.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Zap, BookOpen, Sparkles, Trophy, FileCheck, ClipboardList, Loader2,
} from "lucide-react";

const ACTIONS = [
  {
    id: "core",
    label: "Complete Core Modules",
    icon: BookOpen,
    hint: "Marks video watched + reading read + passes the quiz for every Core Module your current plan grants access to.",
    run: (api) => api.post("/tester-automation/modules/complete", { track: "core" }),
    summary: (d) => {
      const done = d?.completed || 0;
      const total = d?.total || 0;
      const locked = d?.plan_locked || 0;
      const label = done === 1 ? "Core Module" : "Core Modules";
      return locked > 0
        ? `${done} ${label} completed (${total} enabled on your plan; ${locked} require an upgrade).`
        : `${done} ${label} completed.`;
    },
  },
  {
    id: "bonus",
    label: "Complete Bonus Modules",
    icon: Sparkles,
    hint: "Same flow as core, restricted to the Bonus Modules unlocked by your current plan.",
    run: (api) => api.post("/tester-automation/modules/complete", { track: "bonus" }),
    summary: (d) => {
      const done = d?.completed || 0;
      const total = d?.total || 0;
      const locked = d?.plan_locked || 0;
      const label = done === 1 ? "Bonus Module" : "Bonus Modules";
      if (total === 0 && locked === 0) return "No Bonus Modules on this course.";
      if (total === 0 && locked > 0)
        return `No Bonus Modules are enabled on your plan (${locked} require an upgrade).`;
      return locked > 0
        ? `${done} ${label} completed (${total} enabled on your plan; ${locked} require an upgrade).`
        : `${done} ${label} completed.`;
    },
  },
  {
    id: "exam",
    label: "Complete Final Exam",
    icon: Trophy,
    hint: "Answers every question correctly via the standard submit endpoint. Blocked with the same error a founder sees if cores are incomplete or your plan doesn't include the exam.",
    run: (api) => api.post("/tester-automation/final-exam/complete"),
    summary: (d) =>
      d?.already_passed
        ? "Final Exam already passed — nothing to do."
        : `Passed with ${d?.score_pct ?? "?"}% — credential ${d?.credential_id || "issued"}.`,
  },
  {
    id: "blueprint",
    label: "Complete Launch Blueprint",
    icon: FileCheck,
    hint: 'Generates valid random data, saves it as "Generated Data - …" draft in Projects, then submits it for panel review. Only runs if your plan includes the Blueprint.',
    run: (api) => api.post("/tester-automation/blueprint/complete"),
    summary: (d) =>
      `Blueprint submitted — draft "${d?.project_name || "Generated Data"}" saved in Projects.`,
  },
  {
    id: "worksheets",
    label: "Complete Worksheets",
    icon: ClipboardList,
    hint: "Fills every field on every accessible worksheet with valid data + closes the decision gate, then submits. Worksheets on plan-locked modules are skipped.",
    run: (api) => api.post("/tester-automation/worksheets/complete"),
    summary: (d) => {
      const done = d?.submitted || 0;
      const total = d?.total || 0;
      const locked = d?.plan_locked || 0;
      const errs = d?.errors?.length || 0;
      const label = done === 1 ? "worksheet" : "worksheets";
      const errStr = errs ? ` — ${errs} failures.` : ".";
      return locked > 0
        ? `${done} ${label} submitted (${total} enabled on your plan; ${locked} require an upgrade)${errStr}`
        : `${done} of ${total} ${label} submitted${errStr}`;
    },
  },
];


export default function Automation() {
  const { user } = useAuth();
  const nav = useNavigate();
  const role = (user?.role || "").toLowerCase();
  const allowed = role === "tester" || role === "super_admin";

  const [running, setRunning] = useState(null);        // action.id
  const [lastResult, setLastResult] = useState({});     // { [id]: { ok, message } }

  React.useEffect(() => {
    if (user && !allowed) {
      toast.error("Automation is Tester-only.");
      nav("/dashboard", { replace: true });
    }
  }, [user, allowed, nav]);

  const runAction = async (action) => {
    setRunning(action.id);
    try {
      const { data } = await action.run(api);
      const msg = action.summary(data);
      setLastResult((prev) => ({ ...prev, [action.id]: { ok: true, message: msg } }));
      toast.success(msg);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Automation failed.";
      const message = typeof detail === "string" ? detail : JSON.stringify(detail);
      setLastResult((prev) => ({ ...prev, [action.id]: { ok: false, message } }));
      toast.error(message);
    } finally {
      setRunning(null);
    }
  };

  if (!allowed) return null;

  return (
    <AppShell title="Automation">
      <div className="space-y-8" data-testid="automation-page">
        <header className="space-y-2">
          <div className="inline-flex items-center gap-2 rounded-full border border-pink-500/30 bg-pink-500/10 px-3 py-1 text-[10px] uppercase tracking-[0.22em] font-bold text-pink-600 dark:text-pink-400">
            <Zap className="h-3 w-3" /> Tester console
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight">
            Automation
          </h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            One-click through the long completion flows. Every action reuses
            the same public endpoints a real founder uses, so pre-existing
            dependencies (like "cores before exam") still apply — you'll get
            the same error copy if you skip a step.
          </p>
        </header>

        <div className="grid gap-4 sm:grid-cols-2">
          {ACTIONS.map((action) => {
            const Icon = action.icon;
            const isRunning = running === action.id;
            const isDisabled = !!running;
            const result = lastResult[action.id];
            return (
              <div
                key={action.id}
                className="rounded-2xl border border-border bg-card p-5 space-y-3 shadow-sm"
                data-testid={`automation-card-${action.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="rounded-xl bg-muted p-2 shrink-0">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-base">{action.label}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {action.hint}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  disabled={isDisabled}
                  onClick={() => runAction(action)}
                  data-testid={`automation-run-${action.id}`}
                  className="inline-flex items-center justify-center gap-2 w-full rounded-xl bg-primary text-primary-foreground text-sm font-semibold px-4 py-2.5 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  {isRunning ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Running…
                    </>
                  ) : (
                    <>
                      <Zap className="h-4 w-4" />
                      Run
                    </>
                  )}
                </button>

                {result && (
                  <div
                    data-testid={`automation-result-${action.id}`}
                    className={`text-xs rounded-lg px-3 py-2 ${
                      result.ok
                        ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20"
                        : "bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20"
                    }`}
                  >
                    {result.message}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="text-xs text-muted-foreground border-t border-border pt-4">
          Tip: To reset an account, use the Admin Console → Users tools to
          wipe progress / blueprint / worksheets before re-running these.
        </div>
      </div>
    </AppShell>
  );
}
